import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  DialogActions,
  DialogContentText,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { ReactComponent as CloseIcon } from '../../../../assets/accountSetting-assets/x-circle.svg';
import {
  CancelButton,
  CustomCloseIcon,
  CustomDialogContent,
  DialogHeader,
  GridItemsTextField,
  GridItemsTypography,
  OdooPopup,
  SaveButton,
  classes,
  Loader,
  TallyPrimeContent,
  StyledHelpIcon,
} from './utils';
import {
  INVALID_INPUT_REGEX,
  getHelperText,
} from '../../../../constants/staticContent';
import {
  createManualAuthCredential,
  tallyPrimeExport,
  updateCredentialData,
} from '../../../../services/exportApi';
import AddIpAddressInfo from './AddIpAddressInfo';
import TaxMapping from './TaxMapping';

const steps = ['ADD ACCOUNT', 'MAP TAX'];

const TallyPrimeForm = ({
  tallyForm,
  closeTallyForm,
  handleNewAndExport,
  isManual,
  isSetting,
  currentData,
  isEditing = false,
  setCurrentData,
  setIsEditing,
}) => {
  const [formValues, setFormValues] = useState({
    port: '',
    ip_address: '',
    company: '',
    display: '',
  });
  const [activeStep, setActiveStep] = useState(0);
  const [credentialId, setCredentialId] = useState();
  const displayNameFromSessionStorage = sessionStorage.getItem(
    'displayNameForCredential',
  );
  const [formErrors, setFormErrors] = useState({
    ip_address: false,
    port: false,
    company: false,
    display: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tallyIpInstructionPopup, setTallyIpInstructionPopup] = useState(false);
  const [credentialData, setCredentialData] = useState();
  const [openTaxPopup, setOpenTaxPopup] = useState(false);
  const params = useParams();
  const invoiceIds = [params?.id];
  const validateUrl = (ip) => {
    const urlRegex = INVALID_INPUT_REGEX.IP_ADDRESS;
    return urlRegex.test(ip);
  };
  const ValidatePort = (port) => {
    const urlRegex = INVALID_INPUT_REGEX.PORT;
    return urlRegex.test(port);
  };
  const ValidateCompany = (company) => {
    const urlRegex = INVALID_INPUT_REGEX.COMPANY;
    return urlRegex.test(company);
  };
  const onCloseTally = () => {
    setFormErrors({
      ip_address: false,
      port: false,
      company: false,
      display: false,
    });
    setFormValues({
      port: '',
      ip_address: '',
      company: '',
      display: '',
    });
    setOpenTaxPopup(false);
    closeTallyForm();
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue =
      name === 'display' || name === 'company' ? value : value.trim();
    setFormValues({
      ...formValues,
      [name]: trimmedValue,
    });
    if (
      name === 'ipAddress' ||
      name === 'port' ||
      name === 'company' ||
      name === 'display'
    ) {
      const isValidUrl = validateUrl(trimmedValue);
      const isValidatePort = ValidatePort(trimmedValue);
      const isValidateCompany = ValidateCompany(trimmedValue);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ip_address: name === 'ip_address' ? !isValidUrl : prevErrors.ip_address,
        port: name === 'port' ? !isValidatePort : prevErrors.port,
        company: name === 'company' ? !isValidateCompany : prevErrors.company,
        display: name === 'display' ? !trimmedValue : prevErrors.display,
      }));
    }
  };
  useEffect(() => {
    if (isEditing && currentData) {
      setFormValues(currentData.credential);
    }
  }, [currentData]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValidUrl = validateUrl(formValues.ip_address);
    const isValidatePort = ValidatePort(formValues.port);
    const isValidateCompany = ValidateCompany(formValues.company);
    setFormErrors({
      ip_address: !isValidUrl,
      port: !isValidatePort,
      company: !isValidateCompany,
      display: !formValues.display,
    });
    if (
      isValidUrl &&
      isValidatePort &&
      isValidateCompany &&
      formValues.display !== ''
    ) {
      const newCredential = {
        label: 'TallyPrime',
        credential: formValues,
      };
      setCredentialData(newCredential);

      setIsLoading(true);
      if (isEditing) {
        const result = await updateCredentialData({
          id: currentData.id,
          payload: { credential: formValues },
          type: 'TallyPrime',
        });
        if (result) {
          setCredentialId(result?.id);
          setCurrentData(result);
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
          setOpenTaxPopup(true);
        }
      } else {
        const displayName = displayNameFromSessionStorage || formValues.display;
        const credential = await createManualAuthCredential(
          newCredential,
          false,
          displayName,
        );
        sessionStorage.removeItem('displayNameForCredential');
        if (credential) {
          setCredentialId(credential?.id);
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
          setOpenTaxPopup(true);
        }
      }
      setIsLoading(false);
    }
    // onCloseTally();
  };
  return (
    <OdooPopup
      open={tallyForm}
      onClose={onCloseTally}
      aria-labelledby="responsive-dialog-title"
      disableBackdropClick
    >
      {activeStep === 0 ? (
        <>
          <DialogHeader
            id="responsive-dialog-title"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            Connect To Tally Prime
          </DialogHeader>
          <CustomCloseIcon aria-label="close" onClick={onCloseTally}>
            <CloseIcon />
          </CustomCloseIcon>
          <TallyPrimeContent>
            {isLoading && (
              <Loader className={classes.loaderBox}>
                <CircularProgress color="primary" />
              </Loader>
            )}

            <Stepper
              sx={{ marginTop: 2 }}
              activeStep={activeStep}
              alternativeLabel
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Divider sx={{ marginTop: 1 }} />
            <CustomDialogContent>
              <DialogContentText>
                <Box component="form" noValidate autoComplete="off">
                  <Grid container rowGap={4}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        alignItems="center"
                        marginTop={1}
                      >
                        <Typography variant="body1">
                          How to add URL for more information
                        </Typography>
                        <StyledHelpIcon
                          onClick={() => setTallyIpInstructionPopup(true)}
                        />
                      </Grid>
                      <Backdrop
                        className={classes.tallyIntroductionBackdrop}
                        open={tallyIpInstructionPopup}
                      >
                        <AddIpAddressInfo
                          isLoading={isLoading}
                          setTallyIpInstructionPopup={
                            setTallyIpInstructionPopup
                          }
                        />
                      </Backdrop>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <GridItemsTypography variant="body1">
                          Display Name
                        </GridItemsTypography>
                        <GridItemsTextField
                          fullWidth
                          required
                          variant="outlined"
                          type="text"
                          size="small"
                          name="display"
                          value={formValues.display}
                          onChange={handleInputChange}
                          error={formErrors.display}
                          helperText={getHelperText(
                            'display',
                            'display',
                            formErrors,
                            formValues,
                          )}
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <GridItemsTypography variant="body1">
                          Company Name
                        </GridItemsTypography>
                        <GridItemsTextField
                          fullWidth
                          required
                          variant="outlined"
                          type="text"
                          size="small"
                          name="company"
                          value={formValues.company}
                          onChange={handleInputChange}
                          error={formErrors.company}
                          helperText={getHelperText(
                            'company',
                            'company',
                            formErrors,
                            formValues,
                          )}
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <GridItemsTypography variant="body1">
                          URL
                        </GridItemsTypography>
                        <GridItemsTextField
                          fullWidth
                          required
                          variant="outlined"
                          type="text"
                          size="small"
                          name="ip_address"
                          value={formValues.ip_address}
                          onChange={handleInputChange}
                          error={formErrors.ip_address}
                          helperText={getHelperText(
                            'ip_address',
                            'ip_address',
                            formErrors,
                            formValues,
                          )}
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <GridItemsTypography variant="body1">
                          Port
                        </GridItemsTypography>
                        <GridItemsTextField
                          fullWidth
                          required
                          variant="outlined"
                          type="number"
                          size="small"
                          name="port"
                          value={formValues.port}
                          onChange={handleInputChange}
                          error={formErrors.port}
                          helperText={getHelperText(
                            'port',
                            'port',
                            formErrors,
                            formValues,
                          )}
                          autoComplete="off"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContentText>
            </CustomDialogContent>

            <DialogActions>
              <Box className={classes.actionButtonBox} paddingBottom={3}>
                <CancelButton
                  className={classes.cancelButton}
                  onClick={onCloseTally}
                >
                  <Typography variant="body1">Cancel</Typography>
                </CancelButton>
                <SaveButton
                  variant="contained"
                  className={classes.saveButton}
                  onClick={handleSubmit}
                >
                  <Typography variant="body1">Next</Typography>
                </SaveButton>
              </Box>
            </DialogActions>
          </TallyPrimeContent>
        </>
      ) : (
        <TaxMapping
          exportType="TallyPrime"
          credentialId={credentialId}
          invoiceIds={invoiceIds}
          handleNewAndExport={handleNewAndExport}
          newCredential={credentialData}
          isManual={isManual}
          documentExportFunc={tallyPrimeExport}
          openTaxPopup={openTaxPopup}
          closePopup={onCloseTally}
          isSetting={isSetting}
          currentData={currentData}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      )}
    </OdooPopup>
  );
};

TallyPrimeForm.propTypes = {
  tallyForm: PropTypes.bool.isRequired,
  closeTallyForm: PropTypes.func.isRequired,
  handleNewAndExport: PropTypes.func.isRequired,
  isManual: PropTypes.bool.isRequired,
  isSetting: PropTypes.bool.isRequired,
  currentData: PropTypes.shape({
    id: PropTypes.string,
    credential: PropTypes.shape({}),
  }).isRequired,
  isEditing: PropTypes.bool.isRequired,
  setCurrentData: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
};

export default TallyPrimeForm;
